import { Text, View, ScrollView, Pressable } from 'react-native';
import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { ActivityIndicator } from 'react-native-paper';
import { useQueries } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';

import Avatar from '@/components/People/Avatar';
import CutCard from '@/components/General/CutCard';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import BibleStudyTeacherDetail, { TEACHER_TREND_PERIODS } from '@/components/BibleStudies/BibleStudyTeacherDetail';
import { Query, defaultQueryOptions } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { bibleStudyTeacherDetail } from '@/data/bibleStudies';
import { getPersonBasic } from '@/data/people';

const MAX_TEACHERS = 10;

const METRIC_ROWS = [
    {
        key: 'sessions',
        label: 'Sessions taught',
        value: (d) => d?.sessionsTaught?.count ?? '—',
    },
    {
        key: 'students',
        label: 'Distinct students',
        value: (d) => d?.distinctStudentsTaught ?? '—',
    },
    {
        key: 'perWeek',
        label: 'Sessions / week',
        value: (d) => d?.sessionsPerWeek?.sessionsPerWeek ?? '—',
    },
    {
        key: 'regular',
        label: 'Students regular now',
        value: (d) => d?.effectiveness?.percentRegular != null ? `${Math.round(d.effectiveness.percentRegular)}%` : '—',
    },
];

/**
 * Teachers tab body for /biblestudies/reports?tab=teachers. One multi-select
 * rostered-teacher picker drives three states: nothing selected (empty
 * state), one teacher (full effectiveness detail), 2–10 teachers (side-by-side
 * metric compare). Selection lives in the URL (teacherIDs + period) so shared
 * links and shortcuts restore it.
 *
 * 'own'-only callers (rostered teachers without report/manage perms) get the
 * same UI with 'own'-scope detail calls — they're preselected to themselves,
 * and the backend denies foreign-teacher detail (rendered as the inline
 * not-found state).
 *
 * @param {string} roleID - teacher role on the designated Bible Study type.
 * @param {boolean} canViewAllReports - 'all' scope access (View Bible Study
 *   Reports / Manage Bible Studies / admin).
 */
const BibleStudyTeachersPanel = ({ roleID, canViewAllReports = false }) => {
    const router = useRouter();
    const params = useLocalSearchParams();
    const { data: loggedInUser } = Query.people.loggedInUser();

    const scope = canViewAllReports ? 'all' : 'own';

    const [selectedTeachers, setSelectedTeachers] = useState([]);
    const [restored, setRestored] = useState(false);
    const [teacherSearch, setTeacherSearch] = useState('');

    const urlTeacherIds = useMemo(() => {
        const raw = Array.isArray(params?.teacherIDs) ? params.teacherIDs[0] : params?.teacherIDs;
        return String(raw || '').split(',').map(s => s.trim()).filter(Boolean).slice(0, MAX_TEACHERS);
    }, [params?.teacherIDs]);

    const period = TEACHER_TREND_PERIODS.some(p => p.key === params?.period) ? params.period : 'week';

    // Rostered-teacher picker options — same role-contains filter the report
    // teacher filter uses, minus the 'all'-scope gate so 'own'-only callers
    // still get a working picker (People RLS still applies regardless).
    const rosterQuery = Query.people.all({
        filters: roleID ? { conditions: [{ fieldId: 'roles', operator: 'contains_any', value: [roleID] }] } : undefined,
        filterLogic: 'AND',
        searchTerm: teacherSearch,
        options: { enabled: roleID != null },
    });

    // One-shot restore of teacherIDs from a shared link/shortcut. The
    // multi-select needs person objects; getPersonBasic shares the basicById
    // cache used everywhere else.
    const restoreQueries = useQueries({
        queries: (restored ? [] : urlTeacherIds).map(id => ({
            queryKey: QueryKeys.people.basicById(id),
            queryFn: () => getPersonBasic(id),
            staleTime: Infinity,
            ...defaultQueryOptions,
        })),
    });

    useEffect(() => {
        if (restored) return;
        if (urlTeacherIds.length === 0) {
            // Fold of the old index-tab behavior: 'own'-only callers land on
            // their own detail.
            if (!canViewAllReports && loggedInUser?.id) setSelectedTeachers([loggedInUser]);
            setRestored(true);
            return;
        }
        if (restoreQueries.some(q => q.isLoading)) return;
        // Missing person rows still keep their id so the detail RPC can
        // resolve/deny them — chips just render a sparse label.
        setSelectedTeachers(restoreQueries.map((q, i) => q.data || { id: urlTeacherIds[i] }));
        setRestored(true);
    }, [restored, urlTeacherIds, restoreQueries, canViewAllReports, loggedInUser]);

    // Keep the selection in the URL so it survives shares/shortcuts.
    useEffect(() => {
        if (!restored) return;
        const ids = selectedTeachers.map(t => t?.id).filter(Boolean);
        router.setParams({ teacherIDs: ids.length ? ids.join(',') : undefined });
    }, [restored, selectedTeachers, router]);

    const handleTeachersChange = (next) => {
        const arr = (next || []).filter(Boolean);
        if (arr.length > MAX_TEACHERS) {
            Toast.show({ type: 'info', text2: `You can compare up to ${MAX_TEACHERS} teachers at once.` });
            setSelectedTeachers(arr.slice(0, MAX_TEACHERS));
            return;
        }
        setSelectedTeachers(arr);
    };

    const selectedIds = selectedTeachers.map(t => t?.id).filter(Boolean).slice(0, MAX_TEACHERS);

    // Side-by-side metrics — one teacher-detail RPC per teacher via parallel
    // queries; the shared cache makes previously-viewed teachers instant.
    const detailQueries = useQueries({
        queries: (selectedIds.length >= 2 ? selectedIds : []).map(teacherID => ({
            queryKey: QueryKeys.bibleStudies.teacherDetail({ scope, teacherID }),
            queryFn: () => bibleStudyTeacherDetail({ scope, teacherID }),
            staleTime: 1000 * 60 * 5,
            ...defaultQueryOptions,
        })),
    });

    const selector = (
        <View style={styles.selectorWrap} testID="bible-study-reports-teachers-selector">
            <SheetSelector
                multiple
                titleOnly
                type="people"
                queryResult={roleID ? rosterQuery : undefined}
                queryResultSearch={setTeacherSearch}
                title="Teachers"
                placeholder="Select one or more teachers"
                value={selectedTeachers}
                onChange={handleTeachersChange}
            />
        </View>
    );

    if (selectedIds.length === 0) {
        return (
            <View style={styles.container}>
                {selector}
                <EmptyListPlaceholder
                    icon="people-outline"
                    spacing={40}
                    message="Select one or more teachers"
                />
            </View>
        );
    }

    if (selectedIds.length === 1) {
        return (
            <View style={styles.container}>
                {selector}
                {/* period only seeds the embedded chart — the chart's own
                    in-iframe toggle owns switching after render. */}
                <BibleStudyTeacherDetail
                    teacherID={selectedIds[0]}
                    scope={scope}
                    period={period}
                />
            </View>
        );
    }

    return (
        <View style={styles.container}>
            {selector}
            <ScrollView contentContainerStyle={styles.compareScroll} keyboardShouldPersistTaps="handled">
                <CutCard accentColor="primary" style={styles.tableWrap} contentStyle={styles.tableWrapContent} testID="bible-study-teachers-compare-table">
                    {/* Label column */}
                    <View style={styles.labelColumn}>
                        <View style={styles.headerCell} />
                        {METRIC_ROWS.map(row => (
                            <View key={row.key} style={styles.metricCell}>
                                <Text style={styles.metricLabel}>{row.label}</Text>
                            </View>
                        ))}
                    </View>

                    {/* Teacher columns — horizontally scrollable */}
                    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.columnsRow}>
                        {selectedIds.map((teacherID, i) => {
                            const query = detailQueries[i];
                            const detail = query?.data;
                            return (
                                <Pressable
                                    key={teacherID}
                                    testID={`bible-study-teacher-compare-col-${teacherID}`}
                                    accessibilityRole="button"
                                    onPress={() => router.push(`/people/${teacherID}`)}
                                    style={styles.teacherColumn}
                                >
                                    <View style={styles.headerCell}>
                                        <Avatar size={40} profile={{ id: teacherID, firstName: detail?.teacher?.firstName, lastName: detail?.teacher?.lastName }} disableBorder />
                                        <Text style={styles.teacherName} numberOfLines={1}>
                                            {detail?.teacher ? `${detail.teacher.firstName} ${detail.teacher.lastName}` : ' '}
                                        </Text>
                                    </View>
                                    {METRIC_ROWS.map(row => (
                                        <View key={row.key} style={styles.metricCell}>
                                            {query?.isLoading
                                                ? <ActivityIndicator size="small" />
                                                : <Text style={styles.metricValue}>{row.value(detail)}</Text>}
                                        </View>
                                    ))}
                                </Pressable>
                            );
                        })}
                    </ScrollView>
                </CutCard>
            </ScrollView>
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    container: {
        flex: 1,
    },
    selectorWrap: {
        paddingHorizontal: 16,
        paddingTop: 12,
        paddingBottom: 4,
        _web: {
            maxWidth: 400,
        },
    },
    compareScroll: {
        padding: 16,
        paddingTop: 8,
    },
    tableWrap: {
        // CutCard owns surface/radius/overflow; the shadow stays here so it
        // converts to a clip-path drop-shadow on web.
        boxShadow: shadows.card,
        alignSelf: 'flex-start',
        maxWidth: '100%',
        _web: {
            willChange: 'transform',
        },
    },
    tableWrapContent: {
        flexDirection: 'row',
    },
    labelColumn: {
        width: 140,
        borderRightWidth: 1,
        borderRightColor: colors.borderSubtle,
    },
    columnsRow: {
        flexDirection: 'row',
    },
    teacherColumn: {
        width: 150,
        borderRightWidth: 1,
        borderRightColor: colors.borderSubtle,
    },
    headerCell: {
        height: 84,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        paddingHorizontal: 8,
        borderBottomWidth: 1,
        borderBottomColor: colors.borderSubtle,
    },
    teacherName: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.text,
        textAlign: 'center',
    },
    metricCell: {
        height: 52,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 8,
        borderBottomWidth: 1,
        borderBottomColor: colors.borderSubtle,
    },
    metricLabel: {
        fontSize: 12,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        textAlign: 'center',
    },
    metricValue: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: colors.text,
        textAlign: 'center',
    },
}));

export default React.memo(BibleStudyTeachersPanel);
